import React from 'react';
import '@inmoment/the-kitchen'; // Make sure DebugService actually loads
import { TitleText } from '../../../cookbook-ui/custom-components/TitleText/TitleText';
import { Page } from '../../../cookbook-ui/Page_SC';
import styled from 'styled-components';
import { Accordion, AccordionItem, Dropdown, DropdownObject } from '@inmoment/the-kitchen';
import { getLinkTo } from '../../../utils/fns';

const SpacedChildren = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 48 }) => gap}px;
`;

export const DebugService: React.VFC = () => {
  const [dropdownElement, setDropdownElement] = React.useState<HTMLDivElement | null>(null);
  const [hasReproducedExampleBug, setHasReproducedExampleBug] = React.useState(false);
  const [dropdownValue, setDropdownValue] = React.useState<string | string[] | undefined>(undefined);
  const dropdownDebugId = dropdownElement?.getAttribute('data-kitchen-debug-id') ?? null;
  const onValueChange = () => setHasReproducedExampleBug(true);

  return (
    <>
      <Page>
        <SpacedChildren>
          <div>
            <TitleText
              headerText="DebugService"
              isLargeHeader
              footerText={
                <>
                  The Kitchen DebugService is a tool provided to help debug issues in Kitchen components. It is available as the{' '}
                  <code>theKitchen</code> global variable in the browser console.
                </>
              }
            />
          </div>

          <Accordion>
            <AccordionItem open title="How-to">
              <div>
                To debug a Kitchen component with the DebugService, follow these steps:
                <ol>
                  <li>Open the page where the component you are trying to debug is and type `theKitchen.isEnabled = true` into the console</li>
                  <li>Refresh the page</li>
                  <li>Reproduce the issue</li>
                  <li>Use the element inspector to find the Kitchen component that is having issues.</li>
                  <li>
                    Copy the `data-kitchen-debug-id` property of that component to somewhere where you'll be able to access it later. Note that this
                    ID changes each time the component is removed and added back to the DOM.
                  </li>
                  <li>
                    Run <code>theKitchen.download()</code> in the browser console.
                  </li>
                  <li>
                    Post the downloaded file and the data-kitchen-debug-id into the thread of your issue in the #the-kitchen-inquiries slack channel.
                    If you create a Jira ticket, please also attach the file to that ticket.
                  </li>
                </ol>
              </div>
            </AccordionItem>

            <AccordionItem open title="Example">
              <SpacedChildren gap={24}>
                <Dropdown
                  data={[{ id: 'test', title: 'test' }]}
                  value={undefined}
                  internalProps={{ wrapper: { ref: setDropdownElement } }}
                  onValueChange={onValueChange}
                ></Dropdown>

                <div>
                  This {getLinkTo('Dropdown', { target: '_blank' })} has a bug! Reproduce the bug to start debugging:
                  <ol>
                    <li>Open the Dropdown</li>
                    <li>Click the menu item titled "test"</li>
                    <li>
                      <strong style={{ color: 'red' }}>BUG</strong>: The Dropdown does not update with the selected item.
                    </li>
                  </ol>
                </div>

                {hasReproducedExampleBug && (
                  <div>
                    Let's use the DebugService to debug this issue:
                    <ol>
                      <li>Open the browser's dev tools</li>
                      <li>Use the inspect tool to reveal the dropdown in the element view</li>
                      <li>
                        Find the element with the <code>data-debug-kitchen-id</code> attribute that begins with <code>Dropdown-</code>.
                        <ul>
                          <li>
                            This ID should be <code>{dropdownDebugId}</code>
                          </li>
                        </ul>
                      </li>
                      <li>Copy the attribute's value to the clipboard.</li>
                      <li>Open the console.</li>
                      <li>
                        Run <code>const instance = theKitchen.components.mounted['the copied debug id']</code>
                        <ul>
                          <li>
                            Your command should be <code>const instance = theKitchen.components.mounted['{dropdownDebugId}']</code>
                          </li>
                        </ul>
                      </li>
                      <li>
                        Now run <code>instance.renders</code>. This should display a list of each time the component{' '}
                        <a href="https://react.dev/learn/render-and-commit" target="_blank" rel="noreferrer">
                          rendered
                        </a>
                        .
                        {dropdownDebugId && (
                          <ul>
                            <li>
                              Here is a visual representation of the debug service instance's data:{' '}
                              {theKitchen?.components.mounted[dropdownDebugId]?.toJsx()}
                            </li>
                          </ul>
                        )}
                      </li>
                      <li>
                        Look at the first render. It should have the following properties:
                        <ul>
                          <li>startTime: This is when this render started</li>
                          <li>endTime: This is when this render finished</li>
                          <li>props: This is an object containing all the props passed to the object in this render.</li>
                          <li>
                            hooks: This is an object containing all of the internal{' '}
                            <a href="https://react.dev/reference/react" target="_blank" rel="noreferrer">
                              hook
                            </a>{' '}
                            values for this render.
                          </li>
                        </ul>
                      </li>
                      <li>
                        Expand the <code>props</code> property.
                        <ul>
                          <li>
                            <code>props</code> should have a <code>data</code> property that matches the data displayed in the Dropdown.
                          </li>
                          <li>
                            Note that the <code>value</code> prop is <code>undefined</code>. This is because no item was selected when the Dropdown
                            initially rendered.
                          </li>
                        </ul>
                      </li>
                      <li>
                        Now look at the <code>props</code> property of the last render. This render occurred <em>after</em> we tried to select the
                        item in the Dropdown.
                      </li>
                      <li>
                        Note that the <code>value</code> prop is again <code>undefined</code>. This doesn't make sense, because the value should have
                        updated when we selected the Dropdown item.
                      </li>
                      <li>
                        Now look at the value of the <code>onValueChange</code> prop. It reads:
                        <ul>
                          <li>
                            <code>{onValueChange.toString()}</code>
                          </li>
                        </ul>
                      </li>
                      <li>Notice that there is no code here to update the selected value.</li>
                      <li>
                        🎉 We've found the bug! The <code>onValueChange</code> handler needs updated to update the <code>value</code> prop to the new
                        value.
                      </li>
                      <li>
                        The following Dropdown has been updated to correctly update its <code>value</code> when an item is selected. The DebugService
                        can be used to verify that the <code>value</code> prop is updating as expected.
                        <ul>
                          <li>
                            <Dropdown
                              data={[{ id: 'test', title: 'test' }]}
                              value={dropdownValue}
                              onValueChange={({ selected }) => setDropdownValue((selected as DropdownObject).id)}
                            ></Dropdown>
                          </li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                )}
              </SpacedChildren>
            </AccordionItem>

            <AccordionItem title="theKitchen global variable documentation">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>checkMutations</td>
                    <td>boolean</td>
                    <td>Determines whether to check for prop mutations.</td>
                  </tr>
                  <tr>
                    <td>components</td>
                    <td>{'Record<"mounted" | "unmounted", Record<string, DebugService>>'}</td>
                    <td>An object containing components currently in the DOM.</td>
                  </tr>
                  <tr>
                    <td>logOptions</td>
                    <td>{'object'}</td>
                    <td>An object containing log options for all mounted Kitchen components.</td>
                  </tr>
                  <tr>
                    <td>isEnabled</td>
                    <td>boolean</td>
                    <td>Determines whether the DebugService is enabled. If false, no component data will be collected.</td>
                  </tr>
                  <tr>
                    <td>download</td>
                    <td>{'(pretty?: boolean) => void'}</td>
                    <td>Downloads an JSON file containing all component data.</td>
                  </tr>
                  <tr>
                    <td>dump</td>
                    <td>{'() => void'}</td>
                    <td>Opens a page containing all component data in JSON format.</td>
                  </tr>
                  <tr>
                    <td>getComponentTree</td>
                    <td>{'() => object'}</td>
                    <td>Returns an tree-like object of components where each component references its own children.</td>
                  </tr>
                  <tr>
                    <td>view</td>
                    <td>{'() => void'}</td>
                    <td>Displays a modal with debug information for all components.</td>
                  </tr>
                </tbody>
              </table>
            </AccordionItem>

            <AccordionItem title="Component DebugService documentation">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>id</td>
                    <td>string</td>
                    <td>The component's ID used by the DebugService. This is different from the HTML 'id' attribute.</td>
                  </tr>
                  <tr>
                    <td>logOptions</td>
                    <td>object</td>
                    <td>An object containing log options for different stages of this component's life cycle.</td>
                  </tr>
                  <tr>
                    <td>mountTime</td>
                    <td>number</td>
                    <td>The time at which the component mounted.</td>
                  </tr>
                  <tr>
                    <td>renders</td>
                    <td>object[]</td>
                    <td>An array containing data for each render.</td>
                  </tr>
                  <tr>
                    <td>unmountTime</td>
                    <td>number</td>
                    <td>The time at which the component unmounted.</td>
                  </tr>
                  <tr>
                    <td>getElement</td>
                    <td>{'() => HTMLElement | null'}</td>
                    <td>Returns the component's DOM element.</td>
                  </tr>
                  <tr>
                    <td>getParentComponent</td>
                    <td>{'() => DebugService | null'}</td>
                    <td>Returns the component's parent component.</td>
                  </tr>
                  <tr>
                    <td>getParentComponents</td>
                    <td>{'() => Record<string, DebugService> | null'}</td>
                    <td>Returns a subset of mounted components including the component's parent components.</td>
                  </tr>
                  <tr>
                    <td>getChildComponents</td>
                    <td>{'() => Record<string, DebugService> | null'}</td>
                    <td>
                      Returns a subset of mounted components including all components that are children of this component OR children of children.
                    </td>
                  </tr>
                  <tr>
                    <td>getDirectChildComponents</td>
                    <td>{'() => Record<string, DebugService> | null'}</td>
                    <td>Returns a subset of mounted components that are direct children of this component.</td>
                  </tr>
                  <tr>
                    <td>view</td>
                    <td>{'() => void'}</td>
                    <td>Displays a modal with this component's debug information.</td>
                  </tr>
                </tbody>
              </table>
            </AccordionItem>
          </Accordion>
        </SpacedChildren>
      </Page>
    </>
  );
};
